import { type RefObject, useEffect, useRef } from 'react'

export const getKeyboardEvent = (event: Event): KeyboardEvent | undefined =>
  'key' in event ? (event as KeyboardEvent) : undefined

const useEventListener = <Element extends HTMLElement = HTMLDivElement>(
  eventName: keyof WindowEventMap,
  handler: (event: Event) => void,
  element?: RefObject<Element | null>,
): void => {
  // Create a ref that stores handler
  const savedHandler = useRef<(event: Event) => void>(undefined)

  useEffect(() => {
    // Define the listening target
    const targetElement: Element | Window = element?.current || window

    if (typeof targetElement.addEventListener !== 'function') {
      return undefined
    }

    // Update saved handler if necessary
    if (savedHandler.current !== handler) {
      savedHandler.current = handler
    }

    // Create event listener that calls handler function stored in ref
    const eventListener = (event: Event) => {
      // eslint-disable-next-line no-extra-boolean-cast
      if (!!savedHandler.current) {
        savedHandler.current(event)
      }
    }

    targetElement.addEventListener(eventName, eventListener)

    // Remove event listener on cleanup
    return () => {
      targetElement.removeEventListener(eventName, eventListener)
    }
  }, [eventName, element, handler])
}

export default useEventListener
